import { createContext, useState, useEffect ,useRef} from "react";
import { useSelector } from "react-redux";
import {
  loadFiltersFromLocalStorage,
  saveFiltersToLocalStorage,
  removeFiltersFromLocalStorage,
} from "../Components/Common/CommonFunction";
import {
  experienceOptions,
  jobTypeOptions,
  employmentTypeOptions,
} from "../Constant";

export const JobSearchContext = createContext();

function parseCsvOrMulti(params, keys) {
  const out = [];
  const seen = new Set();
  (keys || []).forEach((k) => {
    const allVals = params.getAll(k) || [];
    allVals.forEach((raw) => {
      String(raw || "")
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
        .forEach((v) => {
          if (!seen.has(v)) {
            seen.add(v);
            out.push(v);
          }
        });
    });
  });
  return out;
}

function toSelectList(values) {
  return (values || []).map((v) => ({ value: v, label: String(v) }));
}

function parseJobSearchFiltersFromUrl() {
  try {
    if (typeof window === "undefined" || !window.location) return null;
    const params = new URLSearchParams(window.location.search || "");
    if (!String(params).trim()) return null;

    const keyword =
      (params.get("search") || params.get("keyword") || "").trim();
    const locations = parseCsvOrMulti(params, ["locations", "location"]);
    const industryType = parseCsvOrMulti(params, [
      "industry_type",
      "industry_id[]",
      "industry_id",
    ]);
    const functionalArea = parseCsvOrMulti(params, [
      "functional_area",
      "functional_area_id[]",
      "functional_area_id",
    ]);
    const companies = parseCsvOrMulti(params, ["company_id", "company"]);

    const hasAny =
      !!keyword ||
      locations.length > 0 ||
      industryType.length > 0 ||
      functionalArea.length > 0 ||
      companies.length > 0;
    if (!hasAny) return null;

    return {
      keyword: keyword,
      locations: toSelectList(locations),
      industry_type: toSelectList(industryType),
      functional_area: toSelectList(functionalArea),
      company: toSelectList(companies),
    };
  } catch (e) {
    return null;
  }
}



export default function JobSearchProvider({ children }) {
  const fetchJobseekerDataReducerData = useSelector(
    (state) => state.fetchJobseekerDataReducer
  );

  const [profileData, setProfileData] = useState(null);
  const [filters, setFilters] = useState(null);
  const [pagePerpage, setPagePerpage] = useState({ page: 1, per_page: 10 });
  const shouldSaveToLocal = useRef(false); // Prevent saving default filters
  // Update profile data from Redux state
  useEffect(() => {
    if (
      !fetchJobseekerDataReducerData.isLoading &&
      fetchJobseekerDataReducerData.data &&
      fetchJobseekerDataReducerData.data.data
    ) {
      setProfileData(fetchJobseekerDataReducerData.data.data);
    }
  }, [fetchJobseekerDataReducerData]);

  // Load filters from localStorage or profileData
  useEffect(() => {
    const localFilters = loadFiltersFromLocalStorage("jobSearchFilters", null);
    const urlFilters = parseJobSearchFiltersFromUrl();
    
    if (localFilters) {
      const merged = urlFilters ? { ...localFilters, ...urlFilters } : localFilters;
      if (!merged.verified_status) {
        merged.verified_status = "verified";
      }
      setFilters(merged);
      if (urlFilters) {
        shouldSaveToLocal.current = true;
        saveFiltersToLocalStorage("jobSearchFilters", merged);
      }
    } else if (profileData) {
      const newFilters = {
        keyword: profileData.jobseeker_preferred_job_title || "",
        experience:
          experienceOptions.find(
            (option) => option.value === profileData.total_exp
          ) || "",
        freshness: 30, // Default to 30 days
        locations: profileData.jobseeker_preferred_location || [],
        industry_type: profileData.jobseeker_preferred_industry
          ? [profileData.jobseeker_preferred_industry]
          : [],
        employment_type: profileData.jobseeker_preferred_employment_type
          ? [
              employmentTypeOptions.find(
                (option) =>
                  option.value ===
                  profileData.jobseeker_preferred_employment_type
              ),
            ]
          : [],
        functional_area: profileData.jobseeker_preferred_department
          ? [profileData.jobseeker_preferred_department]
          : [],
        skills: profileData.skill || [],
        company: [],
        verified_status: "verified",
      };
      const merged = urlFilters ? { ...newFilters, ...urlFilters } : newFilters;
      if (!merged.verified_status) {
        merged.verified_status = "verified";
      }
      setFilters(merged);
      saveFiltersToLocalStorage("jobSearchFilters", merged);
    } else {
      // Only set in-memory defaults, don't save
      const defaultFilters = {
        keyword: "",
        experience: "",
        freshness: 30, // Default to 30 days
        locations: [],
        industry_type: [],
        employment_type: [],
        functional_area: [],
        skills: [],
        company: [],
        verified_status: "verified",
      };
      setFilters(urlFilters ? { ...defaultFilters, ...urlFilters } : defaultFilters);
    }
  }, [profileData]);

  

  // Save filters to localStorage on change
  useEffect(() => {
    console.log("filters5",filters);
    if (shouldSaveToLocal.current && filters) {
      saveFiltersToLocalStorage("jobSearchFilters", filters);
    }
  }, [filters]);
  
  const updateFilters = (newFilters,resetPage = false) => {
    shouldSaveToLocal.current = true;
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
    // Reset page to 1 if resetPage is true
  if (resetPage) {
    setPagePerpage({ page: 1, per_page: pagePerpage.per_page });
  }
  };

  const updatePagePerpage = (newFilters) => {
    setPagePerpage((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  
  const clearFilters = () => {
    shouldSaveToLocal.current = true;
    setFilters({
      keyword: "",
      experience: "",
      freshness: 30, // Default to 30 days after clear
      locations: [],
      industry_type: [],
      employment_type: [],
      functional_area: [],
      skills: [],
      company: [],
      verified_status: "verified",
    });
    // removeFiltersFromLocalStorage("jobSearchFilters");
  };

  return (
    <JobSearchContext.Provider
      value={{
        filters,
        updateFilters,
        clearFilters,
        updatePagePerpage,
        pagePerpage,
      }}
    >
      {children}
    </JobSearchContext.Provider>
  );
}
